import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DASH_TOOLTIP } from '../../dashboard-overview/components/tooltipStyle';
import { useReport } from '../hooks/useReport';
import Button from '../../../components/ui/Button';
import {
  DEFAULT_TREND_MONTHS,
  TREND_WINDOW_OPTIONS,
  datesChosen,
  trendCaption,
  trendRequestParams,
} from './trendWindow';

// 'YYYY-MM' -> 'Sep' / 'Sep 2025'
const monthLabel = (m, withYear = false) => {
  if (typeof m !== 'string' || !m.includes('-')) return m;
  const [y, mm] = m.split('-');
  const d = new Date(Number(y), Number(mm) - 1, 1);
  if (Number.isNaN(d.getTime())) return m;
  return d.toLocaleString('en-US', withYear ? { month: 'short', year: 'numeric' } : { month: 'short' });
};

/**
 * Onboarding trend — the whole pipeline, unlike the rest of this report, which covers
 * terminal-state vendors only. TRUE counts (no bulk-import trimming, unlike the dashboard
 * trend). Three lines, each a subset of the one above it:
 *   Registered   every vendor that registered that month
 *   Acknowledged those that reached Level 2, i.e. the vendor accepted the payment terms
 *   Approved     those that are Active today, which happens when a vendor code is assigned
 * The gap between the top two lines is the approval backlog; the gap to the bottom line is
 * vendors approved but still waiting for a code.
 *
 * The counts are true — nothing is dropped from them, so a month here agrees with the rest of the
 * report. The window is chosen with the 6 / 9 month pills (6 by default). A date range set in the
 * report's filter bar always wins, and then the pills are disabled until it is cleared.
 */
const ReportTrendChart = ({ filters }) => {
  const [months, setMonths] = React.useState(DEFAULT_TREND_MONTHS);
  const byDates = datesChosen(filters);
  const { data, loading } = useReport('/reports/mis/trend', trendRequestParams(filters, months));
  const rows = Array.isArray(data) ? data : [];

  // The card's height is set by its grid-row sibling (the donuts + Country
  // column), so a fixed 200px chart left a dead band underneath. Fill whatever
  // height the card actually has — measured ourselves and passed to recharts as
  // a number, since ResponsiveContainer's own height re-measure is unreliable.
  const areaRef = React.useRef(null);
  const [areaH, setAreaH] = React.useState(0);
  React.useEffect(() => {
    const el = areaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setAreaH(el.getBoundingClientRect().height);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    // The sibling column's cards grow as their data arrives, which resizes this
    // card's grid row shortly after mount — settle on the final height even if
    // a ResizeObserver delivery is missed.
    const settle = setTimeout(measure, 600);
    return () => { ro.disconnect(); clearTimeout(settle); };
  }, []);
  const chartH = Math.max(200, Math.floor(areaH));

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2 border-b border-dash-border pb-2">
        <div className="min-w-0">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-dash-foreground">Onboarding Trend</h3>
          <p className="mt-0.5 text-[11px] text-dash-muted-foreground">{trendCaption(filters, months)}</p>
        </div>
        <div
          role="group"
          aria-label="Months shown"
          className="inline-flex shrink-0 gap-0.5 rounded-full border border-dash-border p-0.5"
          title={byDates ? 'A date range is set in the filters above. Clear it to use these.' : undefined}
        >
          {TREND_WINDOW_OPTIONS.map((m) => {
            const active = !byDates && m === months;
            return (
              <Button
                key={m}
                size="xs"
                variant={active ? 'default' : 'ghost'}
                className="rounded-full px-3"
                aria-pressed={active}
                disabled={byDates}
                onClick={() => setMonths(m)}
              >
                {m} months
              </Button>
            );
          })}
        </div>
      </div>
      <div ref={areaRef} className="min-h-[200px] flex-1">
        {loading && rows.length === 0 ? (
          <div className="flex h-full items-center justify-center text-[11px] text-dash-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="flex h-full items-center justify-center text-[11px] text-dash-muted-foreground">No data available</div>
        ) : (
          <ResponsiveContainer width="100%" height={chartH}>
            <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="var(--dash-border)" vertical={false} />
              <XAxis
                dataKey="month"
                tickFormatter={(m) => monthLabel(m)}
                tickLine={false}
                axisLine={{ stroke: 'var(--dash-border)' }}
                tick={{ fontSize: 11, fill: 'var(--dash-muted-foreground)' }}
              />
              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                width={34}
                // "6,000" needs more than the 34px this axis gets; abbreviate so
                // the leading digits can't be clipped off ("6,000" -> "6k").
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : String(v))}
                tick={{ fontSize: 11, fill: 'var(--dash-muted-foreground)' }}
              />
              <Tooltip
                labelFormatter={(m) => monthLabel(m, true)}
                contentStyle={DASH_TOOLTIP.contentStyle}
                labelStyle={DASH_TOOLTIP.labelStyle}
                itemStyle={DASH_TOOLTIP.itemStyle}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="registered" stroke="var(--dash-chart-1)" strokeWidth={2} dot={{ r: 2 }} name="Registered" />
              <Line type="monotone" dataKey="acknowledged" stroke="var(--dash-chart-3)" strokeWidth={2} dot={{ r: 2 }} name="Acknowledged" />
              <Line type="monotone" dataKey="approved" stroke="var(--dash-chart-2)" strokeWidth={2} dot={{ r: 2 }} name="Approved (Active)" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default ReportTrendChart;
